import type { TFunction } from 'i18next';
import numeral from 'numeral';

import type {
  Campaign,
  IEmailABTest,
  MailrailSingleSendEmailCampaign,
  TEmailABTestVariation,
  TFlagsRecord,
} from '@feathr/blackbox';
import { EEmailABTestStatus, EEmailABTestWinningMetric } from '@feathr/blackbox';
import { ETheme } from '@feathr/components';
import type { IEmailABTestColor } from '@feathr/extender/utils/emailABTest';
import {
  getEmailABTest,
  getEmailABTestSentVariation,
  getEmailABTestVariationMailing,
  isEmailABTestAvailable,
} from '@feathr/extender/utils/emailABTest';
import { moment } from '@feathr/hooks';

import type { IABTestActivityPoint, IABTestTimelineProgress } from './ABTestReport.types';

// Display order of the summary's metric rows and the performance chart's toggle.
export const AB_TEST_METRICS: readonly EEmailABTestWinningMetric[] = [
  EEmailABTestWinningMetric.Opens,
  EEmailABTestWinningMetric.Clicks,
  EEmailABTestWinningMetric.Conversions,
];

export const AB_TEST_CHART_HEIGHT = 200;

export const AB_TEST_ACTIVITY_COLORS: Readonly<
  Record<Exclude<keyof IABTestActivityPoint, 'date'>, IEmailABTestColor>
> = {
  clicks: { shade: 600, theme: ETheme.fuchsia },
  deliveries: { shade: 600, theme: ETheme.indigo },
  opens: { shade: 600, theme: ETheme.orange },
  sends: { shade: 600, theme: ETheme.neutral },
};

export function isABTestReportVisible(
  flags: Partial<TFlagsRecord>,
  campaign: Campaign,
): campaign is MailrailSingleSendEmailCampaign {
  return (
    isEmailABTestAvailable(flags, campaign) &&
    getEmailABTest(campaign as MailrailSingleSendEmailCampaign).is_enabled &&
    Boolean(getEmailABTestVariationMailing(campaign as MailrailSingleSendEmailCampaign, 'b'))
  );
}

export function getABTestTimelineProgress(
  startedAt: string | undefined,
  endsAt: string | undefined,
  now: moment.Moment = moment(),
): IABTestTimelineProgress {
  if (!startedAt || !endsAt) {
    return { hoursRemaining: 0, percentComplete: 0 };
  }
  const start = moment.utc(startedAt);
  const end = moment.utc(endsAt);
  const total = end.diff(start);
  const elapsed = Math.min(Math.max(now.diff(start), 0), Math.max(total, 0));
  // Floored so 100% only shows once no time remains.
  const percentComplete = total > 0 ? Math.floor((elapsed / total) * 100) : 100;
  const hoursRemaining = Math.ceil(moment.duration(Math.max(end.diff(now), 0)).asHours());
  return { hoursRemaining, percentComplete };
}

export function getABTestMetricPillStatus(
  variation: TEmailABTestVariation,
  abTest: IEmailABTest,
): EEmailABTestStatus | undefined {
  if (abTest.status === EEmailABTestStatus.Testing) {
    return abTest.status;
  }
  return getABTestVersionPillStatus(variation, abTest);
}

export function getABTestVersionPillStatus(
  variation: TEmailABTestVariation,
  abTest: IEmailABTest,
): EEmailABTestStatus | undefined {
  return getEmailABTestSentVariation(abTest) === variation ? abTest.status : undefined;
}

export function getABTestMetricLabel(metric: EEmailABTestWinningMetric, t: TFunction): string {
  const labelMap: Record<EEmailABTestWinningMetric, string> = {
    [EEmailABTestWinningMetric.Clicks]: t('Click rate'),
    [EEmailABTestWinningMetric.Conversions]: t('Conversion rate'),
    [EEmailABTestWinningMetric.Opens]: t('Open rate'),
  };
  return labelMap[metric];
}

export function formatABTestRate(rate: number): string {
  return numeral(rate).format('0.[0]%');
}

function formatABTestChartDate(date: string, timezone: string | undefined, format: string): string {
  const time = moment.utc(date);
  return (timezone ? time.tz(timezone) : time).format(format);
}

export function formatABTestChartTick(date: string, timezone: string | undefined): string {
  return formatABTestChartDate(date, timezone, 'LT');
}

export function formatABTestChartTooltip(date: string, timezone: string | undefined): string {
  return formatABTestChartDate(date, timezone, 'MMM D, LT z');
}
